/** One notification inside an event node (detailed density).
 *
 *  A single row: switch · name · origin/state chips · channels · overflow menu.
 *  Proposals (id === null) get a dashed border and no actions — there is
 *  nothing to link to or toggle until they are created.
 */
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useState, type MouseEvent, type ReactNode } from 'react';

import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import {
  languagePolicyActions,
  notificationFiresActions,
  rulesetActions,
} from '../notifications/notificationLinks';
import { DeliveryMethodChips } from './DeliveryMethodChips';
import { ORIGIN_HELP, ORIGIN_LABELS, formatFires, relativeTime } from './format';
import type { TimelineActions, TimelineDeliveryMethod, TimelineNotification } from './model/types';
import { TourLinks } from './TourLinks';
import { WarningBadges } from './WarningBadges';

interface Props {
  notification: TimelineNotification;
  /** Detailed density adds a second line: what guards and composes delivery. */
  showDetail?: boolean;
  actions?: TimelineActions;
  stats?: { fires: number; errors: number; lastTs: string | null };
  onInspect?: (nf: TimelineNotification) => void;
  busyNotificationIds?: ReadonlySet<number>;
  busyMethodIds?: ReadonlySet<number>;
}

export function NotificationCard({
  notification: nf,
  showDetail = false,
  actions,
  stats,
  onInspect,
  busyNotificationIds,
  busyMethodIds,
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const proposal = nf.id === null;
  const canToggle = !proposal && Boolean(actions?.onToggleNotification) && nf.enabled !== undefined;
  const busy = nf.id !== null && busyNotificationIds?.has(nf.id);

  const openMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);

  const handleToggleMethod = (dm: TimelineDeliveryMethod, enabled: boolean) => {
    if (nf.id === null || dm.id === null) return;
    void actions?.onToggleDeliveryMethod?.(nf.id, dm.id, enabled);
  };

  const detail = showDetail ? <DetailLine notification={nf} /> : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: detail ? 'flex-start' : 'center',
        gap: 1,
        py: 0.5,
        px: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: proposal ? 'divider' : 'transparent',
        borderStyle: proposal ? 'dashed' : 'solid',
        bgcolor: nf.enabled === false ? 'action.hover' : 'transparent',
        opacity: nf.suppressedByChild ? 0.55 : 1,
        minWidth: 0,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Tooltip
        sx={detail ? { mt: -0.25 } : undefined}
        title={
          proposal
            ? 'Proposed — not created yet'
            : nf.enabled === undefined
              ? 'Not decided'
              : nf.enabled
                ? 'Enabled'
                : 'Disabled'
        }
      >
        <span>
          <Switch
            size="small"
            checked={nf.enabled === true}
            disabled={!canToggle || busy}
            onChange={(e) => {
              if (nf.id !== null) void actions?.onToggleNotification?.(nf.id, e.target.checked);
            }}
            inputProps={{ 'aria-label': `Enable ${nf.name}` }}
          />
        </span>
      </Tooltip>

      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 500, cursor: onInspect ? 'pointer' : 'default' }}
            onClick={() => onInspect?.(nf)}
            title={nf.name}
          >
            {nf.name}
          </Typography>
          {proposal && (
            <Chip size="small" label="proposed" sx={{ height: 18, fontSize: '0.6875rem' }} />
          )}
          {nf.origin !== 'own' &&
            (() => {
              const chip = (
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    nf.tenant && nf.origin === 'inherited'
                      ? `${ORIGIN_LABELS[nf.origin]} · ${nf.tenant.code}`
                      : ORIGIN_LABELS[nf.origin]
                  }
                  sx={{ height: 18, fontSize: '0.6875rem' }}
                />
              );
              // An inherited row is owned by ANOTHER operator — say which, and
              // let the reader go there. A system row has no operator at all.
              return nf.tenant ? (
                <ValueActions chip title={ORIGIN_HELP[nf.origin]} actions={operatorActions(nf.tenant)}>
                  {chip}
                </ValueActions>
              ) : (
                <Tooltip title={ORIGIN_HELP[nf.origin]}>{chip}</Tooltip>
              );
            })()}
          {/* Tour-specific scope, at every density: which tours this is
              limited to is as load-bearing as whether it is enabled. The rule
              separates it from the notification name, which otherwise runs
              straight into the tour ("… (EN) - Airbnb Blank tour"). */}
          {nf.tours?.length ? (
            <Typography
              component="span"
              aria-hidden
              sx={{ color: 'divider', userSelect: 'none', lineHeight: 1 }}
            >
              |
            </Typography>
          ) : null}
          <TourLinks tours={nf.tours} href={actions?.tourHref} />
          <WarningBadges
            warnings={nf.warnings}
            cannotFireReason={nf.cannotFireReason}
            canonicalEvent={nf.canonicalEvent}
            iconOnly
          />
        </Stack>
        {detail}
      </Box>

      <DeliveryMethodChips
        methods={nf.deliveryMethods}
        onToggle={actions?.onToggleDeliveryMethod ? handleToggleMethod : undefined}
        busyIds={busyMethodIds}
      />

      {stats &&
        (() => {
          const caption =
            stats.fires === 0
              ? 'No sends in the last 30 days'
              : `${stats.fires} sends in 30d${stats.errors ? `, ${stats.errors} failed` : ''}${
                  stats.lastTs ? ` · last ${relativeTime(stats.lastTs)}` : ''
                }`;
          const value = (
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                color: stats.fires === 0 ? 'text.disabled' : 'text.secondary',
                minWidth: 32,
                textAlign: 'right',
              }}
            >
              {formatFires(stats.fires)}
            </Typography>
          );
          // The number is a 30-day count, so the grid it opens carries a
          // 30-day window rather than the delivery-log page's default 7.
          return nf.id === null ? (
            <Tooltip title={caption}>{value}</Tooltip>
          ) : (
            <ValueActions title={caption} actions={notificationFiresActions(nf.id)}>
              {value}
            </ValueActions>
          );
        })()}

      {!proposal && (
        <>
          <IconButton size="small" onClick={openMenu} aria-label={`Actions for ${nf.name}`}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
            {onInspect && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  onInspect(nf);
                }}
              >
                <ListItemText>Details</ListItemText>
              </MenuItem>
            )}
            {actions?.notificationHref && nf.id !== null && (
              <MenuItem
                component="a"
                href={actions.notificationHref(nf.id)}
                target="_blank"
                rel="noopener"
                onClick={closeMenu}
              >
                <ListItemIcon>
                  <OpenInNewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Open editor</ListItemText>
              </MenuItem>
            )}
            {actions?.deliveryLogsHref && nf.id !== null && (
              <MenuItem
                component="a"
                href={actions.deliveryLogsHref(nf.id)}
                target="_blank"
                rel="noopener"
                onClick={closeMenu}
              >
                <ListItemIcon>
                  <OpenInNewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delivery logs</ListItemText>
              </MenuItem>
            )}
            {actions?.anatomyHref && nf.id !== null && (
              <MenuItem
                component="a"
                href={actions.anatomyHref(nf.id)}
                target="_blank"
                rel="noopener"
                onClick={closeMenu}
              >
                <ListItemIcon>
                  <OpenInNewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Anatomy</ListItemText>
              </MenuItem>
            )}
            {actions?.onTestRender && nf.id !== null && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  actions.onTestRender!(nf);
                }}
              >
                <ListItemText>Test render</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
}

/** What guards and composes this notification, in one muted line.
 *
 *  The original brief for the detailed density: "for each notification show
 *  rules/rulesets that guard delivery, loaded blocks and what else is important
 *  on the overview screen". Anything absent is simply omitted rather than
 *  printed as a zero — a row saying "0 rules · 0 blocks" is noise, and the
 *  useful signal is which notifications ARE gated.
 */
function DetailLine({ notification: nf }: { notification: TimelineNotification }) {
  // `text` is what the line reads as (also the hover title); `node` is what it
  // renders — the two named records on this line are triggers.
  const parts: { text: string; node?: ReactNode }[] = [];

  const rules = nf.ruleSummary;
  if (rules) {
    if (rules.count === 0) {
      parts.push({ text: 'no rules — matches every event' });
    } else {
      parts.push({
        text: `${rules.count} rule${rules.count === 1 ? '' : 's'} (${
          rules.matchType === 1 ? 'any' : 'all'
        })`,
      });
    }
    for (const rs of rules.rulesets) {
      const text = `ruleset ${rs.codename ?? rs.name}`;
      parts.push(
        rs.id === null
          ? { text }
          : {
              text,
              node: (
                <ValueActions actions={rulesetActions({ id: rs.id, codename: rs.codename })}>
                  {text}
                </ValueActions>
              ),
            },
      );
    }
  }
  if (nf.blockRefCount) {
    parts.push({ text: `${nf.blockRefCount} block${nf.blockRefCount === 1 ? '' : 's'}` });
  }
  if (nf.disableDefaultBlocks) parts.push({ text: 'default blocks off' });
  if (nf.languagePolicy) {
    const text = `lang ${nf.languagePolicy.codename}`;
    const id = nf.languagePolicy.id;
    parts.push(
      id === null
        ? { text }
        : {
            text,
            node: (
              <ValueActions
                actions={languagePolicyActions({ id, codename: nf.languagePolicy.codename })}
              >
                {text}
              </ValueActions>
            ),
          },
    );
  }
  if (nf.audience && !nf.audience.guests && nf.audience.roles.length === 0 && !nf.audience.observers) {
    parts.push({ text: 'no audience selected' });
  }

  if (!parts.length) return null;
  const title = parts.map((p) => p.text).join(' · ');
  return (
    <Typography
      variant="caption"
      noWrap
      sx={{ display: 'block', color: 'text.secondary', minWidth: 0 }}
      title={title}
    >
      {parts.map((p, i) => (
        <Fragment key={p.text}>
          {i > 0 && ' · '}
          {p.node ?? p.text}
        </Fragment>
      ))}
    </Typography>
  );
}
